import type { Schemas } from '#shopware'
import { type Ref, computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDocumentVisibility, useIntersectionObserver } from '@vueuse/core'
import { useShopwareContext } from '#imports'

const LIVE_ORDER_COUNT_POLL_INTERVAL_MS = 60_000
const CLOCK_TICK_INTERVAL_MS = 1_000

export type CampaignDealStatus = 'none' | 'upcoming' | 'active' | 'soldOut' | 'expired'

/**
 * Drives a product's campaign deal module (PDP/PLP). `promotionDetails` comes from the
 * regular, HTTP-cached product response and stays valid for discount/dates, but its
 * `orderCount` can be up to a minute stale (route cache, see docs/FEATURES.md § Performance).
 * This fetches the live count client-side via the uncached order-count route on mount and
 * every minute after, so a sold-out deal switches state even while a visitor stays on the page.
 *
 * `status` is also re-derived every second off a ticking `now` ref (not a bare `new Date()`
 * read), so a deal flips to `expired` right when its countdown reaches zero — not only when
 * `orderCount` happens to change on some later poll.
 *
 * The order-count poll only runs while the module is both on screen (IntersectionObserver on
 * the returned `elementRef`) and the tab is in the foreground — a listing slider with many deal
 * cards, or a tab left open in the background, would otherwise hammer the uncached order-count
 * route for nothing. `isPolling` ANDs both signals into one flag, so re-entering viewport and
 * tab at once still only triggers a single catch-up fetch, not two — and that catch-up respects
 * the poll interval, so repeatedly toggling a tab cannot fetch faster than once a minute either.
 * The clock only needs the
 * tab-foreground gate: `status` has to keep ticking towards `active`/`expired` even while the
 * module itself isn't rendered yet (e.g. still `upcoming`), so it isn't paused by viewport state.
 */
export const useCampaignDeal = (product: Ref<Schemas['Product'] | null | undefined>) => {
    const { apiClient } = useShopwareContext()

    const promotionDetails = computed(() => product.value?.extensions?.promotionDetails)

    const liveOrderCount = ref<number | null>(null)
    const now = ref(new Date())
    const elementRef = ref<HTMLElement | null>(null)
    let pollHandle: ReturnType<typeof setInterval> | null = null
    let catchUpHandle: ReturnType<typeof setTimeout> | null = null
    let clockHandle: ReturnType<typeof setInterval> | null = null
    let lastFetchAt: number | null = null

    const isInViewport = ref(false)
    useIntersectionObserver(elementRef, ([entry]) => {
        isInViewport.value = entry?.isIntersecting ?? false
    })

    const documentVisibility = useDocumentVisibility()
    const isTabVisible = computed(() => documentVisibility.value === 'visible')
    const isPolling = computed(() => isInViewport.value && isTabVisible.value)

    const orderCount = computed(() => liveOrderCount.value ?? promotionDetails.value?.orderCount ?? 0)

    const status = computed<CampaignDealStatus>(() => {
        const details = promotionDetails.value
        if (!details?.promotionId || details.discount == null) return 'none'
        if (!details.validFrom || !details.validUntil) return 'none'

        const validFrom = new Date(details.validFrom)
        const validUntil = new Date(details.validUntil)

        if (now.value < validFrom) return 'upcoming'
        if (now.value >= validUntil) return 'expired'

        const max = details.maxRedemptionsGlobal
        if (max != null && orderCount.value >= max) return 'soldOut'

        return 'active'
    })

    // Keep showing a deal that just sold out (so "0 of N left" still renders) — everything
    // else (upcoming/expired/none) falls back to the regular, deal-free presentation.
    const isVisible = computed(() => status.value === 'active' || status.value === 'soldOut')

    // Only a capped promotion has an availability to show — an uncapped one is still a perfectly
    // valid, active deal (see `status`), it just has no "x of y left" to report. Both numbers
    // travel together so callers need one check instead of two.
    const availability = computed(() => {
        const max = promotionDetails.value?.maxRedemptionsGlobal
        if (max == null) return null
        return { max, remaining: Math.max(0, max - orderCount.value) }
    })

    async function fetchLiveOrderCount (promotionId: string) {
        lastFetchAt = Date.now()

        try {
            const { data } = await apiClient.invoke('getPromotionOrderCount post /promotion/order-count', {
                body: { promotionIds: [promotionId] }
            })

            const liveCount = data?.orderCounts?.[promotionId]
            if (liveCount != null) {
                liveOrderCount.value = liveCount
            }
        } catch (error) {
            console.error(`Failed to fetch live campaign order count for promotion ${promotionId}`, error)
        }
    }

    function stopPolling () {
        if (pollHandle != null) {
            clearInterval(pollHandle)
            pollHandle = null
        }

        if (catchUpHandle != null) {
            clearTimeout(catchUpHandle)
            catchUpHandle = null
        }
    }

    function startPolling (promotionId: string) {
        stopPolling()
        pollHandle = setInterval(() => fetchLiveOrderCount(promotionId), LIVE_ORDER_COUNT_POLL_INTERVAL_MS)
    }

    // Resume without ever fetching more often than the poll interval: a tab toggled back and
    // forth, or a card scrolled in and out, would otherwise fire a request on every return.
    // If the last fetch is still fresh, wait out the remainder of the minute first.
    function resumePolling (promotionId: string) {
        const sinceLastFetch = lastFetchAt == null ? Infinity : Date.now() - lastFetchAt

        if (sinceLastFetch >= LIVE_ORDER_COUNT_POLL_INTERVAL_MS) {
            fetchLiveOrderCount(promotionId)
            startPolling(promotionId)
            return
        }

        stopPolling()
        catchUpHandle = setTimeout(() => {
            catchUpHandle = null
            fetchLiveOrderCount(promotionId)
            startPolling(promotionId)
        }, LIVE_ORDER_COUNT_POLL_INTERVAL_MS - sinceLastFetch)
    }

    function stopClock () {
        if (clockHandle != null) {
            clearInterval(clockHandle)
            clockHandle = null
        }
    }

    function startClock () {
        stopClock()
        clockHandle = setInterval(() => {
            now.value = new Date()
        }, CLOCK_TICK_INTERVAL_MS)
    }

    // Start/stop polling as the module enters/leaves the viewport or the tab goes to the
    // background.
    watch(isPolling, (polling) => {
        stopPolling()
        if (!polling) return

        const promotionId = promotionDetails.value?.promotionId
        if (!promotionId) return
        resumePolling(promotionId)
    })

    // Catch `now` up immediately on tab return (not after the next tick) so `status` doesn't
    // lag behind a countdown that already expired while the tab was backgrounded.
    watch(isTabVisible, (visible) => {
        if (!visible) {
            stopClock()
            return
        }

        now.value = new Date()
        startClock()
    })

    // Re-sync when the shown product changes (e.g. variant switch on the PDP).
    watch(() => promotionDetails.value?.promotionId, (promotionId, previousPromotionId) => {
        if (promotionId === previousPromotionId) return

        liveOrderCount.value = null
        stopPolling()

        if (!promotionId || !isPolling.value) return
        fetchLiveOrderCount(promotionId)
        startPolling(promotionId)
    })

    // Client-only: the point is to bypass the route cache the initial SSR response already went
    // through, and to avoid leaving a `setInterval` running on the server.
    onMounted(() => {
        if (isTabVisible.value) {
            now.value = new Date()
            startClock()
        }
    })

    onBeforeUnmount(() => {
        stopPolling()
        stopClock()
    })

    return {
        promotionDetails,
        isVisible,
        availability,
        elementRef
    }
}
